import {
  Center,
  Flex,
  Icon,
  Link,
  SkeletonCircle,
  type FlexProps,
} from '@chakra-ui/react';
import { BsGlobe } from 'react-icons/bs';
import { KText } from '@/ui-library/components/text';
import { candidateLinksConfig } from './candidateLinksConfig';

type UrlMap = Record<string, unknown> | null | undefined;

export interface CandidateSocialLinksProps extends Omit<FlexProps, 'children'> {
  /** Contact `social` object — every `*_url` key on it is rendered. */
  social?: UrlMap;
  /** Contact `ats` object — the source-ATS `*_url` keys (Lever / Greenhouse / Ashby). */
  ats?: UrlMap;
  iconSize?: string;
  /** Shown when the contact has no links at all. Omit to render nothing. */
  emptyText?: string;
  /**
   * Contact record still in flight. Holds one icon's worth of space so the row
   * this sits in doesn't reflow when the links land — the contact can only be
   * fetched after its id arrives, so they always resolve a beat late.
   */
  isLoading?: boolean;
}

const collectUrls = (source: UrlMap) =>
  Object.entries(source ?? {})
    .filter(
      ([key, value]) =>
        key.endsWith('_url') && typeof value === 'string' && value.trim() !== ''
    )
    .map(([type, url]) => ({ type, url: url as string }));

/** Unknown `*_url` keys sort last (and render with the generic globe icon). */
const configOrder = (type: string) => {
  const index = candidateLinksConfig.findIndex((config) => config.id === type);
  return index === -1 ? candidateLinksConfig.length : index;
};

/**
 * Read-only row of a candidate's social / source links as icon buttons.
 *
 * Shared so the surfaces that show a candidate outside the contact page — the
 * interview header, the contact info block — render the same icon set from the
 * same config instead of each re-deriving `*_url` keys.
 */
export const CandidateSocialLinks = ({
  social,
  ats,
  iconSize = '16px',
  emptyText,
  isLoading = false,
  ...wrapperProps
}: CandidateSocialLinksProps) => {
  const links = [...collectUrls(social), ...collectUrls(ats)].sort(
    (a, b) => configOrder(a.type) - configOrder(b.type)
  );

  if (isLoading) {
    return (
      <Flex alignItems="center" gap="8px" {...wrapperProps}>
        <SkeletonCircle size={iconSize} />
      </Flex>
    );
  }

  if (!links.length && !emptyText) return null;

  return (
    <Flex alignItems="center" gap="8px" {...wrapperProps}>
      {links.length ? (
        links.map(({ type, url }) => {
          const { icon = BsGlobe, color = '#000' } =
            candidateLinksConfig.find((config) => config.id === type) || {};
          return (
            <Link
              key={type}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${type.replace('_url', '')} profile`}
              _focus={{ outline: 'none' }}
              onClick={(e) => e?.stopPropagation()}
            >
              <Center>
                <Icon
                  as={icon}
                  color={color}
                  width={iconSize}
                  height={iconSize}
                />
              </Center>
            </Link>
          );
        })
      ) : (
        <KText fontSize="sm" color="secondary-700">
          {emptyText}
        </KText>
      )}
    </Flex>
  );
};
